import React, { useMemo } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import { IMAGES } from '@/assets/images'
import PatternItemCard from './PatternItemCard'
import { useGetPublicTopics } from '@/hooks/api/dashboard/useGetPublicTopics'
import { TopicType } from '@/utils/constants'
import { Topic } from '@/types'

const splitIntoColumns = (items: any[], itemsPerColumn = 3): any[][] => {
  const columns: any[][] = []

  for (let i = 0; i < items.length; i += itemsPerColumn) {
    columns.push(items.slice(i, i + itemsPerColumn))
  }
  return columns
}

const ROWS_COUNT = 3
const SPEED = 300

const BOX_WIDTH = {
  xs: 160, // mobile
  sm: 200, // tablet
  md: 280, // small desktop
  lg: 300, // desktop
  xl: 300, // large desktop
}

const COL_GAP = {
  xs: 1.25,
  sm: 1.5,
  md: 2,
  lg: 5,
  xl: 5,
}

const COL_ITEM_GAP = {
  xs: 1.25,
  sm: 1.5,
  md: 2,
  lg: 4,
  xl: 4,
}

const GuestPatternsFeed = () => {
  const {
    data: topics = [],
    isLoading,
    error,
  } = useGetPublicTopics({
    pageNo: 1,
    interaction: 0,
    topicType: TopicType.PATTERN,
  })

  const columns = useMemo(() => {
    let result: any = []
    if (topics.length > 0) result = splitIntoColumns(topics, ROWS_COUNT)
    return result
  }, [topics])

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        py: 1,
      }}
    >
      {isLoading ? (
        // Loading state
        <Grid
          container
          spacing={3}
          sx={{
            width: '100%',
            margin: 0,
          }}
        >
          {Array.from(Array(20)).map((_, index) => (
            <Grid key={`loading-${index}`} item xs={12} sm={6} md={4} lg={2}>
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: 1,
                  borderRadius: '12px',
                  backgroundColor: '#f3f4f6',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: COL_GAP,
            width: 'max-content',
            animation: `scrollGallery ${SPEED}s linear infinite`,

            '@keyframes scrollGallery': {
              from: {
                transform: 'translateX(0)',
              },
              to: {
                transform: 'translateX(-50%)',
              },
            },
          }}
        >
          {[...columns, ...columns].map((column, columnIndex) => (
            <Box
              key={columnIndex}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: COL_ITEM_GAP,

                // staggered effect
                mt: columnIndex % 2 === 0 ? 0 : 5,
              }}
            >
              {column.map((topic: Topic, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    width: BOX_WIDTH,
                    aspectRatio: 1,
                    display: 'block',
                    objectFit: 'cover',
                  }}
                >
                  <PatternItemCard topic={topic} />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default GuestPatternsFeed
